import { useKeycloak } from "@react-keycloak/web";
import { useNavigate } from "react-router";
import { DashboardFilters } from "../components/DashboardFilters";
import { DashboardList } from "../components/DashboardList";
import { CloneProjectModal } from "../components/CloneProjectModal";
import { Pagination } from "../components/common/Pagination";
import { useEnrichedProjects, useProjectsPage, useProjectsStore } from "../state/projects";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { LIST_DEFAULT_SIZE, SortParam } from "../types/pagination";
import { useMyAccess } from "../hooks/useMyAccess";
import { canOwnProjects } from "../helpers/projectPermissions";

/** Maps each dashboard sort option to the backend `sort` parameter. */
const SORT_PARAMS: Record<string, SortParam[]> = {
  name: [{ field: 'name', direction: 'asc' }],
  name_desc: [{ field: 'name', direction: 'desc' }],
  status: [{ field: 'status_id', direction: 'asc' }],
  created_date: [{ field: 'start_date', direction: 'desc' }],
  created_date_desc: [{ field: 'start_date', direction: 'asc' }],
  updated_date: [{ field: 'end_date', direction: 'desc' }],
  updated_date_desc: [{ field: 'end_date', direction: 'asc' }],
  risk: [{ field: 'risk_level', direction: 'asc' }],
  risk_desc: [{ field: 'risk_level', direction: 'desc' }],
};

export const ProjectsPage = (
  { showMenu, setShowMenu }:
    { showMenu: boolean, setShowMenu?: React.Dispatch<React.SetStateAction<boolean>>, mapReload?: boolean }
) => {

  const { keycloak: { token } } = useKeycloak();
  const navigate = useNavigate();
  const { myAccess } = useMyAccess();
  const canClone = canOwnProjects(myAccess);
  const [cloneTarget, setCloneTarget] = useState<ProjectInterface | null>(null);
  const { loadProjectsListPage } = useProjectsPage();
  const listProjects = useProjectsStore((state) => state.listProjects);
  const listPagination = useProjectsStore((state) => state.listPagination);
  // Enriched with action items so the risk filter matches the map view exactly.
  const projectsData = useEnrichedProjects(listProjects);
  const [sortBy, setSortBy] = useState<string>('name');
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(LIST_DEFAULT_SIZE);
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Reload the server page whenever paging or sorting changes.
  useEffect(() => {
    async function loadProjects() {
      try {
        setIsLoading(true);
        await loadProjectsListPage({
          page,
          size,
          sort: SORT_PARAMS[sortBy],
          risk: riskLevelFilter !== 'all' ? riskLevelFilter : undefined,
        });
      } catch (error) {
        toast.error('Failed to load projects', {
          toastId: 'failed-to-load-projects'
        });
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
    if (token) {
      loadProjects();
    }
  }, [token, page, size, sortBy, riskLevelFilter]);

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1);
  };

  const handleRiskFilterChange = (value: string) => {
    setRiskLevelFilter(value);
    setPage(1);
  };

  const handleSizeChange = (value: number) => {
    setSize(value);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading projects..." />
      </div>
    );
  }

  return (
    <>
      <DashboardFilters
        setShowMenu={setShowMenu}
        showMenu={showMenu}
        view={'projects'}
        onSortChange={handleSortChange}
        onPriorityFilter={handleRiskFilterChange}
        selectedRiskLevel={riskLevelFilter}
      />
      <DashboardList
        servers={projectsData}
        riskSort={{
          direction: sortBy === 'risk' ? 'asc' : sortBy === 'risk_desc' ? 'desc' : null,
          onToggle: () => handleSortChange(sortBy === 'risk' ? 'risk_desc' : 'risk'),
        }}
        renderRowActions={canClone ? (project) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setCloneTarget(project);
            }}
            className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
          >
            Clone
          </button>
        ) : undefined}
      />
      <Pagination
        page={listPagination.page || page}
        size={listPagination.size || size}
        total={listPagination.total}
        onPageChange={setPage}
        onSizeChange={handleSizeChange}
      />

      <CloneProjectModal
        isOpen={!!cloneTarget}
        onClose={() => setCloneTarget(null)}
        sourceProject={cloneTarget}
        onCloned={(newProjectId) => {
          setCloneTarget(null);
          navigate(`/projects/${newProjectId}`);
        }}
      />
    </>
  );
};
